<template>
  <div>
    <Alert v-if="planIsScheduledForCancellation" type="info" class="mt-4">
      {{ $t("Scheduled cancellation on") }} {{ plan.scheduledCancellation.date }}
    </Alert>

    <ErrorBanner v-if="planIsSuspended" error-level="error">
      {{ $t("Your contract has been suspended, please settle your situation from the Invoices tab") }}
    </ErrorBanner>

    <ErrorBanner v-else-if="!paymentsUpToDate(plan)" error-level="error">
      {{ $t("You are not up to date with your payments, please settle your situation from the Invoices tab") }}
    </ErrorBanner>

    <div class="bg-white shadow-sm overflow-hidden sm:rounded-lg">
      <div class="bg-white px-4 py-5 sm:px-6">
        <div class="-ml-4 -mt-4 flex justify-between items-center flex-wrap sm:flex-nowrap">
          <div class="ml-4 mt-4">
            <div class="flex items-center">
              <div class="shrink-0">
                <img v-if="plan.imageUrl" class="max-h-24 object-contain" :src="plan.imageUrl" alt="" />
              </div>
              <div class="ml-4">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  {{ plan.title }}
                </h3>
                <p class="text-sm text-gray-500">
                  <a href="#">{{ plan.products?.[0]?.title }}</a>
                </p>
                <p class="text-sm text-gray-500">
                  <a href="#">{{ plan.vendor }}</a>
                </p>
              </div>
            </div>
          </div>
          <div class="w-full sm:w-auto sm:ml-4 mt-4 shrink-0 flex flex-row items-center justify-end">
            <a
              :href="plan.planSaleContract?.marketingPage"
              target="_blank"
              rel="noopener noreferrer"
              class="relative inline-flex items-center px-4 py-2 shadow-xs text-sm font-medium text-button-font-primary bg-primary uppercase focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <DocumentTextIcon class="-ml-1 mr-2 h-5 w-5 text-button-font-primary" aria-hidden="true" />
              <span>{{ $t("View terms") }}</span>
            </a>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
        <dl class="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Contract ID") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">{{ plan.friendlyId }}</dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500 flex items-center">
              {{ $t("Purchase date") }}
              <VTooltip theme="info-tooltip">
                <InformationCircleIcon
                  class="cursor-pointer h-5 w-4 text-gray-400 inline-block ml-1"
                  aria-hidden="true"
                />
                <template #popper>
                  <span>{{ t("purchase_date_tooltip") }}</span>
                </template>
              </VTooltip>
            </dt>
            <dd class="mt-1 text-sm text-gray-900">{{ plan.purchaseDate }}</dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500 flex items-center">
              {{ $t("Contract start date") }}
              <VTooltip theme="info-tooltip">
                <InformationCircleIcon
                  class="cursor-pointer h-5 w-4 text-gray-400 inline-block ml-1"
                  aria-hidden="true"
                />
                <template #popper>
                  <span>{{ t("contract_start_date_tooltip") }}</span>
                </template>
              </VTooltip>
            </dt>
            <dd class="mt-1 text-sm text-gray-900">{{ plan.date }}</dd>
          </div>
          <div class="sm:col-span-1" v-if="!hasBreakdownCoverage(plan)">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Expires on") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">{{ plan.expirationDate }}</dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Warranty Price") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">
              {{ formattedPlanPrice }}
            </dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Payment frequency") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">{{ $t(paymentFrequency) }}</dd>
          </div>
          <div class="sm:col-span-1" v-if="plan.monthly">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Payment method") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900 flex items-center">
              <span data-test="payment-method-value">{{ displayedPaymentMethod }}</span>
              <button
                type="button"
                class="ml-2 text-gray-400 hover:text-gray-600"
                :aria-label="$t(paymentMethodToggleLabel)"
                :disabled="paymentMethodLoading"
                data-test="payment-method-toggle"
                @click="togglePaymentMethod"
              >
                <component :is="paymentMethodToggleIcon" class="h-5 w-4" aria-hidden="true" />
              </button>
            </dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Warranty Term") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900 flex items-center">
              {{ warrantyTerm(plan) }}
              <VTooltip v-if="hasBreakdownCoverage(plan)" theme="info-tooltip">
                <InformationCircleIcon
                  class="cursor-pointer h-5 w-4 text-gray-400 inline-block ml-1"
                  aria-hidden="true"
                />
                <template #popper>
                  <span>
                    La garantie panne prend le relais à la fin de la garantie légale ou commerciale du fabricant, elle
                    vous couvre ensuite pendant
                    {{ warrantyTerm(plan) }}
                  </span>
                </template>
              </VTooltip>
            </dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Status") }}
            </dt>
            <dd class="mt-1">
              <span
                :class="[
                  planIsActive ? 'text-green-800 bg-green-100' : 'text-gray-800 bg-gray-100',
                  'px-2 py-1 text-xs font-medium rounded-full',
                ]"
              >
                {{ $t(statusLabel(plan)) }}
              </span>
            </dd>
          </div>
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Subscribed formula") }}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">{{ plan.categoryName }}</dd>
          </div>
          <div class="sm:col-span-2" v-if="planCoverageNames.length">
            <dt class="text-sm font-medium text-gray-500">
              {{ $t("Included coverages") }}
            </dt>
            <dd class="mt-1 flex flex-wrap gap-2">
              <span
                v-for="coverage in planCoverageNames"
                :key="coverage"
                class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"
              >
                {{ $t(`plan_sale.coverage.${coverage}`) }}
              </span>
            </dd>
          </div>
        </dl>
        <div class="mt-10" v-if="plan.planSaleContract?.maxInsurableCombinedPrice && composedItemsMutable">
          <p class="text-gray-700 my-2">
            Vous utilisez actuellement
            <span class="font-bold">{{ planComposedItemsPrice.toString().replace(/\./g, ",") }}€</span>
            sur {{ plan.planSaleContract?.maxInsurableCombinedPrice }}€ disponibles
          </p>
          <div class="w-full h-6 bg-gray-200 rounded-full">
            <div class="h-6 bg-primary rounded-full" :style="`width: ${planComposedItemsPricePercentage}%`"></div>
          </div>
        </div>
      </div>
      <div
        v-if="planIsActive && !planIsScheduledForCancellation"
        class="border-t border-gray-100 px-4 py-3 sm:px-6 flex justify-end"
      >
        <router-link
          :to="`/plans/${plan.friendlyId}/cancellation`"
          class="text-sm text-gray-500 hover:text-red-500 underline transition-colors"
        >
          {{ $t("Cancel my contract") }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPrice, Alert } from "@estaly/ui";
import { DocumentTextIcon, EyeIcon, EyeOffIcon, InformationCircleIcon } from "@heroicons/vue/solid";
import { mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import ErrorBanner from "@/components/utils/ErrorBanner.vue";
import PlanSalesService from "@/api/services/planSales";

export default {
  props: ["plan"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      paymentMethod: null,
      paymentMethodLoaded: false,
      paymentMethodRevealed: false,
      paymentMethodLoading: false,
    };
  },

  watch: {
    "plan.id"() {
      this.paymentMethod = null;
      this.paymentMethodLoaded = false;
      this.paymentMethodRevealed = false;
    },
  },

  computed: {
    ...mapGetters("planSales", [
      "warrantyTerm",
      "paymentsUpToDate",
      "isActive",
      "isSuspended",
      "hasBreakdownCoverage",
      "coverageNames",
      "statusLabel",
      "planPrice",
      "composedItemsPrice",
      "composedItemsPricePercentage",
      "paymentFrequencyKey",
      "paymentMethodLabel",
    ]),
    ...mapGetters("plan", ["isComposedItemsMutable"]),
    planIsActive() {
      return this.isActive(this.plan);
    },
    planIsScheduledForCancellation() {
      return this.plan.scheduledCancellation?.date;
    },
    cancellationAvailable() {
      return this.isActive(this.plan) && this.plan.cancellationAvailable;
    },
    composedItemsMutable() {
      return this.isComposedItemsMutable(this.plan);
    },
    planComposedItemsPrice() {
      return this.composedItemsPrice(this.plan);
    },
    planComposedItemsPricePercentage() {
      return this.composedItemsPricePercentage(this.plan);
    },
    planIsSuspended() {
      return this.isSuspended(this.plan);
    },
    planCoverageNames() {
      return this.coverageNames(this.plan);
    },
    formattedPlanPrice() {
      return this.planPrice(this.plan);
    },
    paymentFrequency() {
      return this.paymentFrequencyKey(this.plan);
    },
    displayedPaymentMethod() {
      if (!this.paymentMethodRevealed) return "••••";
      return this.paymentMethodLabel(this.paymentMethod) ?? this.$t("Payment method unavailable");
    },
    paymentMethodToggleIcon() {
      return this.paymentMethodRevealed ? EyeOffIcon : EyeIcon;
    },
    paymentMethodToggleLabel() {
      return this.paymentMethodRevealed ? "Hide payment method" : "Show payment method";
    },
  },

  components: {
    Alert,
    ErrorBanner,
    DocumentTextIcon,
    InformationCircleIcon,
  },

  methods: {
    formatPrice,

    async togglePaymentMethod() {
      if (this.paymentMethodRevealed) {
        this.paymentMethodRevealed = false;
        return;
      }

      if (!this.paymentMethodLoaded) await this.retrievePaymentMethod();
      this.paymentMethodRevealed = true;
    },

    async retrievePaymentMethod() {
      const planSaleId = this.plan.id;

      this.paymentMethodLoading = true;
      const response = await PlanSalesService.getPaymentMethod(planSaleId);
      if (response?.success && this.plan?.id === planSaleId) {
        this.paymentMethod = response.data.payment_method;
        this.paymentMethodLoaded = true;
      }
      this.paymentMethodLoading = false;
    },
  },
};
</script>

<i18n src="./InsuranceInformation.i18n.json"></i18n>
